import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Package, Users, AlertTriangle, Download, Info } from 'lucide-react';
import { useOrdenTrabajo, useCreateOt, useGenerateOtPdf, useGenerateOtXlsx, type OtDetailDTO } from '../../hooks/useOrdenesTrabajo';
import { useProject } from '../../hooks/useProjects';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, StatusBadge } from '../../components/ui/Badge';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';

const OT_TABS = ['Items de Obra', 'Materia Prima', 'Equipo de Trabajo'] as const;
type OtTab = (typeof OT_TABS)[number];

export function OrdenTrabajoPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const canCreate = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const { data: project, isLoading: projectLoading } = useProject(id ?? '');
  const { data: ot, isLoading: otLoading, error: otError } = useOrdenTrabajo(id ?? '');
  const createOtMutation = useCreateOt();
  const generatePdf = useGenerateOtPdf();
  const generateXlsx = useGenerateOtXlsx();

  const [activeTab, setActiveTab] = useState<OtTab>('Items de Obra');

  if (projectLoading || otLoading) {
    return (
      <div className="space-y-6 fade-up">
        <SkeletonTable rows={5} cols={4} />
      </div>
    );
  }

  // OT doesn't exist yet — show creation prompt
  if (!ot && otError) {
    return (
      <div className="space-y-6 fade-up">
        <div className="flex items-center gap-4">
          <Link to={`/proyectos/${id}`} className="text-gray-400 hover:text-brand-dark">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-brand-dark">Orden de Trabajo</h1>
        </div>

        <Card>
          <div className="text-center py-12 space-y-4">
            <FileText className="w-16 h-16 text-gray-300 mx-auto" />
            <h2 className="text-lg font-semibold text-brand-dark">
              No existe Orden de Trabajo
            </h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Para crear la OT, el proyecto debe tener una proforma aprobada.
              Los maestros se pueden asignar antes o después.
            </p>
            {canCreate && (
              <Button
                variant="primary"
                onClick={async () => {
                  try {
                    await createOtMutation.mutateAsync(id ?? '');
                  } catch (err: unknown) {
                    const message = (err as { response?: { data?: { error?: { message?: string } } } })
                      ?.response?.data?.error?.message ?? 'Error al crear la Orden de Trabajo';
                    toast('error', message);
                  }
                }}
                isLoading={createOtMutation.isPending}
              >
                Crear Orden de Trabajo
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (!ot) return null;

  return (
    <div className="space-y-6 fade-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/proyectos/${id}`} className="text-gray-400 hover:text-brand-dark">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-brand-dark">Orden de Trabajo</h1>
          <p className="text-sm text-gray-500">
            {project?.codigo} &middot; {project?.client?.nombre}
          </p>
        </div>
        <StatusBadge entity="project" status={ot.estado} />
      </div>

      {/* Banner — no prices */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-800">
        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <p className="text-sm">
          Este documento no incluye precios. Para uso del equipo de campo unicamente.
        </p>
      </div>

      {/* Export buttons */}
      <div className="flex gap-3">
        <Button
          variant="secondary"
          size="sm"
          isLoading={generatePdf.isPending}
          disabled={generateXlsx.isPending}
          onClick={() => generatePdf.mutate(id ?? '')}
        >
          <Download className="w-4 h-4 mr-1" />
          Descargar PDF
        </Button>
        <Button
          variant="secondary"
          size="sm"
          isLoading={generateXlsx.isPending}
          disabled={generatePdf.isPending}
          onClick={() => generateXlsx.mutate(id ?? '')}
        >
          <Download className="w-4 h-4 mr-1" />
          Descargar Excel
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {OT_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-[#3c98ff] text-[#3c98ff]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'Items de Obra' && <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" />}
            {tab === 'Materia Prima' && <Package className="w-4 h-4 inline mr-1.5 -mt-0.5" />}
            {tab === 'Equipo de Trabajo' && <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />}
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'Items de Obra' && <ItemsTab ot={ot} />}
      {activeTab === 'Materia Prima' && <MateriaPrimaTab ot={ot} />}
      {activeTab === 'Equipo de Trabajo' && <EquipoTab ot={ot} />}
    </div>
  );
}

// ── Items de Obra Tab ──────────────────────────────────────────────

function ItemsTab({ ot }: { ot: OtDetailDTO }) {
  if (ot.items.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-500 text-center py-6">Sin items de obra</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4">Codigo</th>
              <th className="py-2 pr-4">Descripcion</th>
              <th className="py-2 pr-4">Medida</th>
              <th className="py-2 text-right">Cantidad</th>
            </tr>
          </thead>
          <tbody>
            {ot.items.map((item) => (
              <tr key={item.id} className="border-b border-gray-50">
                <td className="py-2 pr-4">
                  <Badge color="blue">{item.codigo}</Badge>
                </td>
                <td className="py-2 pr-4 text-gray-700">
                  {item.descripcion_proyecto || item.descripcion}
                </td>
                <td className="py-2 pr-4 text-gray-500">{item.unidad}</td>
                <td className="py-2 text-right font-medium">{item.cantidad.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Materia Prima Tab ──────────────────────────────────────────────

function MateriaPrimaTab({ ot }: { ot: OtDetailDTO }) {
  if (ot.materia_prima.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-500 text-center py-6">Sin materia prima calculada</p>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 italic">
        Esta lista es el insumo para el area de Compras.
      </p>
      <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Cantidades calculadas automaticamente: cantidad_por_unidad (APU) x cantidad (proforma)
        </p>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">SKU</th>
                <th className="py-2 pr-4">Material</th>
                <th className="py-2 pr-4">Presentacion</th>
                <th className="py-2 text-right">Cantidad Total</th>
              </tr>
            </thead>
            <tbody>
              {ot.materia_prima.map((mp) => (
                <tr key={mp.id} className="border-b border-gray-50">
                  <td className="py-2 pr-4 text-gray-500 font-mono text-xs">
                    {mp.material_sku || '—'}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{mp.material_nombre}</td>
                  <td className="py-2 pr-4 text-gray-500">{mp.material_unidad}</td>
                  <td className="py-2 text-right font-medium">
                    {mp.cantidad_total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Equipo de Trabajo Tab ──────────────────────────────────────────

function EquipoTab({ ot }: { ot: OtDetailDTO }) {
  return (
    <div className="space-y-4">
      {/* Residente */}
      <Card>
        <h3 className="text-sm font-semibold text-brand-dark mb-2">Residente de Obra</h3>
        {ot.equipo_trabajo.residente ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#3c98ff]/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-[#3c98ff]" />
            </div>
            <span className="text-sm font-medium">{ot.equipo_trabajo.residente.nombre}</span>
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">Sin residente asignado</p>
        )}
      </Card>

      {/* Maestros */}
      <Card>
        <h3 className="text-sm font-semibold text-brand-dark mb-2">
          Maestros ({ot.equipo_trabajo.maestros.length})
        </h3>
        {ot.equipo_trabajo.maestros.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Sin maestros asignados</p>
        ) : (
          <div className="space-y-3">
            {ot.equipo_trabajo.maestros.map((m) => (
              <div key={m.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                  <Users className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <span className="text-sm font-medium">{m.nombre}</span>
                  <div className="flex gap-1 mt-0.5">
                    {m.especialidades.map((esp) => (
                      <Badge key={esp} color="blue">{esp}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
